import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useLazyQuery } from '@apollo/client/react'
// hocs
import { useApolloClient } from 'hooks/useApolloClient'
// types
import { IOption } from 'components/FormElements/Select'
import { NetworkStatus } from '@apollo/client'
import { FieldType, IField, ILog } from 'types/graphql'
// components
import Row from './components/Row'
import { LoaderWithOverlay, Table } from 'components/UI'
// constants
import { INITIAL_PER_PAGE } from 'constants/table'
// graphql
import { FIELD_LOGS_QUERY } from 'graphql/field/queries'
// views
import { EmptyPlaceholder } from './views'
// utils
import { get, isBoolean } from 'lodash'
import { getFieldsOptions } from 'utils/index'

interface Props {
  field: IField
  riskId?: string
  fieldValueId?: string
}

const HEADERS = [
  { id: 'value', sortable: false, left: true, label: 'PROPERTY VALUE' },
  { id: 'ownedBy', sortable: false, left: true, label: 'SOURCE' },
  { id: 'createdAt', sortable: false, left: true, label: 'DATE' }
]

const prepareLogs = (data): ILog[] =>
  get(data, 'logs.data', []).filter((item: ILog) => isBoolean(item.metadata.value) || item.metadata.value)

const FieldHistoryTable: React.FC<Props> = ({ field, riskId, fieldValueId }) => {
  const router = useRouter()

  const [page, setPage] = React.useState(0)
  const [options, setOptions] = React.useState<IOption[]>([])
  const [perPage, setPerPage] = React.useState(INITIAL_PER_PAGE)

  const { client } = useApolloClient()

  const [fetchLogs, { data, called, loading, networkStatus, refetch }] = useLazyQuery(FIELD_LOGS_QUERY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  })

  const isInitialLoading = loading && networkStatus === NetworkStatus.loading

  const logs = React.useMemo(() => (data ? prepareLogs(data) : []), [data])
  const totalCount: number = get(data, 'logs.pagination.totalCount', 0)

  const handleSetPage = (page: number) => {
    setPage(page)

    refetch({
      filter: {
        entityId: fieldValueId,
        metadataEntityId: router.query.id
      },
      pagination: {
        pageSkip: page * perPage,
        pageTake: perPage
      }
    })
  }

  const handleSetPerPage = (perPage: number) => {
    setPerPage(perPage)

    refetch({
      filter: {
        entityId: fieldValueId,
        metadataEntityId: router.query.id
      },
      pagination: {
        pageSkip: page * perPage,
        pageTake: perPage
      }
    })
  }

  const getFieldOptions = async (fieldLogs: ILog[]) => {
    const options = await getFieldsOptions({
      client,
      isRisk: true,
      fields: [field],
      fieldsValues: fieldLogs.map((item) => ({
        fieldName: field.name,
        fieldValue: item.metadata.value,
        fieldType: field.fieldType
      }))
    })

    setOptions(options)
  }

  const getTableData = async () => {
    const res = await fetchLogs({
      variables: {
        filter: {
          entityId: fieldValueId,
          metadataEntityId: riskId || router.query.id
        },
        pagination: {
          pageSkip: 0,
          pageTake: 20
        }
      }
    })

    const logs = prepareLogs(res.data)

    if (field.fieldType === FieldType.Lookup) {
      await getFieldOptions(logs)
    }
  }

  React.useEffect(() => {
    if (fieldValueId) {
      void getTableData()
    }
  }, [field.id, fieldValueId])

  if ((!called && fieldValueId) || isInitialLoading) {
    return <LoaderWithOverlay isLoading height='calc(100vh - 200px)' />
  }

  if (called && !logs.length) {
    return <EmptyPlaceholder>No history records yet</EmptyPlaceholder>
  }

  return (
    <Table
      data={logs}
      page={page}
      perPage={perPage}
      headers={HEADERS}
      totalCount={totalCount}
      onPageChange={handleSetPage}
      isLoading={networkStatus === 2}
      onPerPageChange={handleSetPerPage}>
      {({ data }) => data.map((item) => <Row key={item.id} data={item} field={field} options={options} />)}
    </Table>
  )
}

export default FieldHistoryTable
